import {useState, useContext} from 'react';
import {useNavigate} from 'react-router-dom';
import {AccountRegistrationContext} from '../contexts/account-registration.provider';
import {OrganizationContext} from '../contexts/organization.provider';
import {useAuth} from '../contexts/auth.provider';
import {getPendingAccount} from '../utils';
import {OrganizationCreateParams, OrganizationServiceImpl, AccountServiceImpl} from '../services';
import {AccountOrganizationRole} from '../models';

interface OrganizationData {
  name: string;
  email: string;
  company: string;
  portalUrl: string;
}

export const useEmailVerificationFlow = (accountId?: string, isLogin?: boolean, email?: string) => {
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string>();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [organizationData, setOrganizationData] = useState<OrganizationData | null>(null);

  const {resendVerificationEmail, verifyEmailOtp} = useContext(AccountRegistrationContext);
  const {createAccountOrganization} = useContext(OrganizationContext);
  const {login, markAsVerified, setOrganizationInfo} = useAuth();

  const organizationService = new OrganizationServiceImpl();
  const accountService = new AccountServiceImpl();

  const resendCode = async () => {
    if (!accountId) {
      setError('Account ID not available. Please try signing up again.');
      return;
    }

    setIsResending(true);
    setError(undefined);

    try {
      const result = await resendVerificationEmail(accountId);
      if (!result.data) {
        throw new Error(result.error || 'Failed to resend verification code');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleLoginOrganizationFetch = async (userAccountId: string): Promise<void> => {
    try {
      const response = await accountService.getAccountOrganizations(userAccountId, 1, 5);
      if (response.hasData() && response.data && response.data.length > 0) {
        const firstAccountOrganization = response.data[0];
        if (firstAccountOrganization.organization) {
          setOrganizationInfo(firstAccountOrganization.organization, firstAccountOrganization);
        }
      }
    } catch (fetchError) {
      console.error('Failed to fetch account organizations:', fetchError);
    }
  };

  const handleOrganizationCreation = async (pendingAccount: any): Promise<boolean> => {
    try {
      const createParams = new OrganizationCreateParams(
        pendingAccount.organizationName,
        pendingAccount.accountId,
        undefined,
        pendingAccount.portalSubdomain,
      );
      const newOrganization = await organizationService.createOrganization(createParams);

      try {
        const accountOrganization = await createAccountOrganization(
          pendingAccount.accountId,
          newOrganization.id,
          AccountOrganizationRole.ADMIN,
        );

        setOrganizationInfo(newOrganization, accountOrganization);

        setOrganizationData({
          name: `${pendingAccount.firstName || ''} ${pendingAccount.lastName || ''}`.trim(),
          email: pendingAccount.email,
          company: pendingAccount.organizationName,
          portalUrl: `${pendingAccount.portalSubdomain}.recco.joy`,
        });

        setShowSuccessModal(true);
        return true;
      } catch (aoError: any) {
        console.error('Failed to create AccountOrganization:', aoError);
        return false;
      }
    } catch (orgError: any) {
      console.error('Failed to create organization:', orgError);
      return false;
    }
  };

  const createUserData = (userAccountId: string, userEmail?: string) => {
    if (isLogin) {
      return {
        id: userAccountId,
        email: userEmail || '',
        firstName: '',
        lastName: '',
        isVerified: true,
      };
    }

    const pendingAccount = getPendingAccount();
    return pendingAccount
      ? {
          id: pendingAccount.accountId,
          email: pendingAccount.email,
          firstName: pendingAccount.firstName,
          lastName: pendingAccount.lastName,
          isVerified: true,
        }
      : undefined;
  };

  const handleLoginFlow = async (userAccountId: string) => {
    await handleLoginOrganizationFetch(userAccountId);
    navigate('/dashboard');
  };

  const handleSignupFlow = async () => {
    const pendingAccountForOrganization = getPendingAccount();
    const shouldCreateOrganization =
      pendingAccountForOrganization?.organizationName &&
      pendingAccountForOrganization?.portalSubdomain &&
      pendingAccountForOrganization?.accountId;

    if (shouldCreateOrganization) {
      const success = await handleOrganizationCreation(pendingAccountForOrganization);
      if (success) return;
    }

    navigate('/dashboard');
  };

  const verifyCode = async (cleanCode: string) => {
    if (!accountId) {
      setError('Account ID not available. Please try signing up again.');
      return;
    }

    setIsVerifying(true);
    setError(undefined);

    try {
      const result = await verifyEmailOtp(accountId, cleanCode);

      if (!result.data?.token) {
        setError(result.error ?? 'The email OTP is invalid or has expired.');
        return;
      }

      const userData = createUserData(accountId, email);
      login(result.data.token, userData);
      markAsVerified();

      if (isLogin) {
        await handleLoginFlow(accountId);
        return;
      }

      await handleSignupFlow();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid verification code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleGoToDashboard = () => {
    setShowSuccessModal(false);
    navigate('/dashboard');
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate('/dashboard');
  };

  return {
    isVerifying,
    isResending,
    error,
    showSuccessModal,
    organizationData,
    resendCode,
    verifyCode,
    setError,
    handleGoToDashboard,
    handleCloseModal,
  };
};
